// Copyright ©️ 2026 eVolve MEP, LLC

import { useState, type ReactNode } from 'react';

import { Button } from '@mantine/core';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';

import { PermissionGuideTable } from './PermissionGuideTable';
import { shopPermissionsData } from './shopPermissionData';
import type { RoleOption } from './types';

const revitRowData = [
  {
    permissionLabel: 'Receives company controlled user configs',
    admin: { assigned: true },
    powerUser: { assigned: true },
    advancedUser: { assigned: true },
    modeler: { assigned: true },
  },
  {
    permissionLabel: 'Upload configurations and files',
    admin: { assigned: true },
    powerUser: { assigned: true },
    advancedUser: { assigned: false },
    modeler: { assigned: false },
  },
  {
    permissionLabel: 'Access to EVOLVE Content Portal',
    admin: { assigned: true },
    powerUser: { assigned: true },
    advancedUser: { assigned: false },
    modeler: { assigned: false },
  },
  {
    permissionLabel: 'Edit project configurations',
    permissionTooltip: (
      <>
        To restrict configuration editing, set a User Group to <b>Read Only</b>
      </>
    ),
    admin: { assigned: true },
    powerUser: { assigned: true },
    advancedUser: { assigned: true },
    modeler: { assigned: true },
  },
] as const satisfies {
  permissionLabel: string;
  admin: RoleOption;
  powerUser: RoleOption;
  advancedUser: RoleOption;
  modeler: RoleOption;
  permissionTooltip?: ReactNode;
}[];

export const PermissionGuideModal = () => {
  const [opened, setOpened] = useState(false);

  const numColumns = Math.max(...[shopPermissionsData, revitRowData].map((d) => Object.keys(d[0]).length - 1));
  return (
    <>
      <Button variant="light" onClick={() => setOpened(true)}>
        Permission guide
      </Button>

      <WrappedModal
        opened={opened}
        hideModalButtons
        onClose={() => setOpened(false)}
        size={850}
        title="Permission guide"
      >
        <PermissionGuideTable
          title="Company Permissions"
          roleData={[
            {
              permissionLabel: 'Can assign licenses',
              admin: { assigned: true },
            },
          ]}
          roleNames={['Admin']}
          roleOrder={['admin']}
          numColumns={numColumns}
        />

        <PermissionGuideTable
          title="Revit Permissions"
          roleData={revitRowData}
          roleNames={['Admin', 'Power User', 'Advanced User', 'User Roles 1-5']}
          roleOrder={['admin', 'powerUser', 'advancedUser', 'modeler']}
          numColumns={numColumns}
        />

        <PermissionGuideTable
          title="Shop Permissions"
          roleData={shopPermissionsData}
          roleNames={['Admin', 'Full Access', 'Shop Floor', 'Limited Access']}
          roleOrder={['Company.Administrator', 'Shop.FullAccess', 'Shop.ShopFloor', 'Shop.LimitedAccess']}
          numColumns={numColumns}
        />
      </WrappedModal>
    </>
  );
};
